/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Snackbar,
    Alert,
    Pagination,
    PaginationItem,
    InputAdornment,
} from '@mui/material';
import Sidebar from '../../Components/Sidebar/Sidebar';
import Navbar from '../../Components/Navbar/Navbar';
import { financeAPI } from '../../services/apiService';
import PersianNumber2Words from '../../Components/PersianNumber2Words/PersianNumber2Words';
import './Finance.css';

function Finance() {
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.resolvedLanguage || i18n.language || 'en';
    const isPersian = currentLanguage.toLowerCase().startsWith('fa');
    const locale = isPersian ? 'fa-IR' : 'en-US';
    const formatPaginationNumber = (value) =>
        new Intl.NumberFormat(locale, {
            useGrouping: false,
        }).format(Number(value));
    const [orders, setOrders] = useState([]);
    const [balance, setBalance] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [openWithdrawDialog, setOpenWithdrawDialog] = useState(false);
    const [withdrawData, setWithdrawData] = useState({ amount: '' });

    const [openDepositDialog, setOpenDepositDialog] = useState(false);
    const [depositData, setDepositData] = useState({ amount: '' });

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const rowsPerPage = 10;

    const formatPrice = (value) => Number(value || 0).toLocaleString(locale);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [ordersRes, balanceRes] = await Promise.all([
                    financeAPI.getOrders(),
                    financeAPI.getBalance(),
                ]);

                const rawOrders = ordersRes?.data ?? ordersRes ?? [];
                const sortedOrders = [...rawOrders].sort(
                    (a, b) => new Date(b.created_at) - new Date(a.created_at)
                );

                const toNumber = (v) => {
                    if (typeof v === 'number' && Number.isFinite(v)) return v;
                    if (typeof v === 'string') return Number(v.replace(/[^\d.-]/g, '')) || 0;
                    if (v && typeof v === 'object') {
                        const val =
                            v?.parsedValue ??
                            v?.data?.balance?.parsedValue ??
                            v?.balance?.parsedValue ??
                            v?.balance ??
                            0;
                        return toNumber(val);
                    }
                    return 0;
                };
                setOrders(sortedOrders);
                setBalance(toNumber(balanceRes));
            } catch (err) {
                console.error(err);
                setError(t('finance_fetch_error'));
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [t]);

    const handleOpenWithdrawDialog = () => {
        setWithdrawData({ amount: '', card: '' });
        setOpenWithdrawDialog(true);
    };

    const handleCloseWithdrawDialog = () => setOpenWithdrawDialog(false);

    const handleWithdrawChange = (e) => {
        const { name, value } = e.target;

        if (name === 'amount') {
            const numericValue = value.replace(/[^\d]/g, '');
            if (Number(numericValue) > balance) return;
            const formatted = Number(numericValue).toLocaleString('en-US');
            setWithdrawData((prev) => ({ ...prev, amount: formatted }));
            return;
        }

        if (name === 'card') {
            let digits = value.replace(/\D/g, '');
            digits = digits.slice(0, 16);
            const formatted = digits.replace(/(\d{4})(?=\d)/g, '$1-');
            setWithdrawData((prev) => ({ ...prev, card: formatted }));
            return;
        }

        setWithdrawData((prev) => ({ ...prev, [name]: value }));
    };

    const handleFillMax = () => {
        setWithdrawData((prev) => ({ ...prev, amount: balance.toLocaleString('en-US') }));
    };

    const handleConfirmWithdraw = () => {
        financeAPI.withdraw(withdrawData);
        setSnackbar({ open: true, message: t('withdraw_request_submitted'), severity: 'success' });
        handleCloseWithdrawDialog();
    };

    const handleOpenDepositDialog = () => {
        setDepositData({ amount: '', card: '' });
        setOpenDepositDialog(true);
    };

    const handleCloseDepositDialog = () => setOpenDepositDialog(false);

    const handleDepositChange = (e) => {
        const { name, value } = e.target;

        if (name === 'amount') {
            const numericValue = value.replace(/[^\d]/g, '');
            if (Number(numericValue) > 999999999999) return;
            const formatted = Number(numericValue).toLocaleString('en-US');
            setDepositData((prev) => ({ ...prev, amount: formatted }));
            return;
        }

        if (name === 'card') {
            let digits = value.replace(/\D/g, '');
            digits = digits.slice(0, 16);
            const formatted = digits.replace(/(\d{4})(?=\d)/g, '$1-');
            setDepositData((prev) => ({ ...prev, card: formatted }));
        }
    };

    const handleConfirmDeposit = () => {
        financeAPI.deposit(depositData);
        setSnackbar({ open: true, message: t('deposit_request_submitted'), severity: 'success' });
        handleCloseDepositDialog();
    };

    const handleCloseSnackbar = (_, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar((prev) => ({ ...prev, open: false }));
    };
    const filteredOrders = orders.filter((order) =>
        String(order.uid || '')
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
    );
    const pageCount = Math.ceil(filteredOrders.length / rowsPerPage);
    const paginatedOrders = filteredOrders.slice((page - 1) * rowsPerPage, page * rowsPerPage);

    return (
        <div className="finance_page">
            <Sidebar />
            <div className="finance_main">
                <Navbar />
                <div className="finance_content">
                    <div className="finance_header">
                        <Button
                            variant="contained"
                            className="withdraw_btn"
                            onClick={handleOpenWithdrawDialog}
                        >
                            {t('withdraw_from_account')}
                        </Button>
                        <Button
                            variant="contained"
                            className="deposit_btn"
                            onClick={handleOpenDepositDialog}
                        >
                            {t('deposit_to_account')}
                        </Button>
                    </div>
                    <TextField
                        placeholder={t('search')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        variant="outlined"
                        size="small"
                        className="search_bar"
                        sx={{ marginRight: 2 }}
                    />
                    {loading && <p>{t('loading')}</p>}
                    {error && (
                        <p>
                            {t('error')}:{error}
                        </p>
                    )}
                    {!loading && !error && (
                        <>
                            <TableContainer component={Paper} className="finance_table">
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="center">
                                                {t('transaction_number')}
                                            </TableCell>
                                            <TableCell align="center">{t('amount')}</TableCell>
                                            <TableCell align="center">{t('paid_status')}</TableCell>
                                            <TableCell align="center">{t('type')}</TableCell>
                                            <TableCell align="center">{t('description')}</TableCell>
                                            <TableCell align="center">
                                                {t('created_date')}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paginatedOrders.map((order) => (
                                            <TableRow key={order.uid}>
                                                <TableCell
                                                    align="center"
                                                    data-label={t('transaction_number')}
                                                >
                                                    {order.uid}
                                                </TableCell>
                                                <TableCell align="center" data-label={t('amount')}>
                                                    {formatPrice(order.amount)}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    data-label={t('paid_status')}
                                                >
                                                    {order.is_paid ? t('yes') : t('no')}
                                                </TableCell>
                                                <TableCell align="center" data-label={t('type')}>
                                                    {t(`finance_types.${order.type}`) || order.type}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    data-label={t('description')}
                                                >
                                                    {order.description}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    data-label={t('created_date')}
                                                >
                                                    {new Date(order.created_at).toLocaleDateString(
                                                        locale
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {filteredOrders.length > rowsPerPage && (
                                <Pagination
                                    count={pageCount}
                                    page={page}
                                    onChange={(_, value) => setPage(value)}
                                    className="orders_pagination"
                                    sx={{
                                        marginTop: 2,
                                        display: 'flex',
                                        justifyContent: 'center',
                                    }}
                                    hidePrevButton
                                    hideNextButton
                                    renderItem={(item) => (
                                        <PaginationItem
                                            // eslint-disable-next-line react/jsx-props-no-spreading
                                            {...item}
                                            page={
                                                item.type === 'page' && item.page !== null
                                                    ? formatPaginationNumber(item.page)
                                                    : item.page
                                            }
                                            aria-label={
                                                item.type === 'page'
                                                    ? `${t('page')} ${formatPaginationNumber(
                                                          item.page
                                                      )}`
                                                    : undefined
                                            }
                                        />
                                    )}
                                />
                            )}
                        </>
                    )}

                    <Dialog
                        open={openWithdrawDialog}
                        onClose={handleCloseWithdrawDialog}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle>{t('withdraw_from_account')}</DialogTitle>
                        <DialogContent>
                            <TextField
                                margin="dense"
                                placeholder={t('withdraw_from_account')}
                                name="amount"
                                value={withdrawData.amount}
                                onChange={handleWithdrawChange}
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    inputProps: { min: 0, max: balance },
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Button
                                                size="small"
                                                variant="text"
                                                onClick={handleFillMax}
                                                sx={{ fontSize: '0.8rem', padding: 0 }}
                                            >
                                                {t('withdraw_all_balance')}
                                            </Button>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ marginBottom: 1 }}
                            />
                            {withdrawData.amount && (
                                <DialogContentText sx={{ color: 'gray', mt: 1 }}>
                                    <PersianNumber2Words value={withdrawData.amount} />
                                </DialogContentText>
                            )}
                            <DialogContentText>
                                {t('current_balance')}: {formatPrice(balance)}
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button type="button" onClick={handleCloseWithdrawDialog} color="error">
                                {t('cancel')}
                            </Button>
                            <Button type="button" onClick={handleConfirmWithdraw} color="success">
                                {t('submit_withdraw')}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog
                        open={openDepositDialog}
                        onClose={handleCloseDepositDialog}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle>{t('deposit_to_account')}</DialogTitle>
                        <DialogContent>
                            <TextField
                                margin="dense"
                                placeholder={t('deposit_to_account')}
                                name="amount"
                                value={depositData.amount}
                                onChange={handleDepositChange}
                                fullWidth
                                variant="outlined"
                                sx={{ marginBottom: 1 }}
                            />
                            {depositData.amount && (
                                <DialogContentText
                                    className="deposit_text"
                                    sx={{ color: 'gray', mt: 1 }}
                                >
                                    <PersianNumber2Words value={depositData.amount} />
                                </DialogContentText>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button type="button" onClick={handleCloseDepositDialog} color="error">
                                {t('cancel')}
                            </Button>
                            <Button type="button" onClick={handleConfirmDeposit} color="success">
                                {t('submit_deposit')}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={4000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    >
                        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </div>
            </div>
        </div>
    );
}

export default Finance;
